import React, { useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { formatCurrency, formatPercentage } from '../shared/utils'
import { formatDate } from '../shared/utils'
import { useContext } from 'react'
import { AuthenticationContext } from '../../Auth/authentication.context'
import originsDirect from '../../assets/images/ORIGINS-DIRECT.png'
// import originsManagement from '../../assets/images/ORIGINS-MANAGEMENT.png'
import originsDirectInstant from '../../assets/images/ORIGINS-DIRECT-INSTANT.png'
import Modals from '../shared/Modals'
import server from '../../server'
import { Message } from 'primereact/message'

export default function OrderDetailsHeader(props) {
  // Presentational: the parent (OrderDetails) owns the header row so the status
  // badge/annotation re-render after an admin cancels from the bottom toolbar.
  const order = [props.order]

  const { isAdmin, user } = useContext(AuthenticationContext)

  // Show PA Amount / PA Percentage at the top of the order detail page only
  // when the pharmacy is NOT "Origins Direct only" — i.e. it is signed up to
  // Origins Management or is a member of a pharmacy group. Admins keep the
  // existing visibility. This is independent of order status, so eligible
  // pharmacies see PA on all orders, not just Confirmed ones.
  const isPartOfGroup = Boolean(
    user?.pharmacy?.pharmacyGroup || user?.pharmacyGroup || user?.pharmacyGroupId
  )
  const hasOriginsManagement = Boolean(
    user?.pharmacyModules?.find((module) => module.name === 'Origins Management')
  )
  const canSeePa = isAdmin || hasOriginsManagement || isPartOfGroup

  const [paymentModalVisible, setPaymentModalVisible] = useState(false)
  const [paymentDetails, setPaymentDetails] = useState(null)
  const [paymentLoading, setPaymentLoading] = useState(false)

  const fetchPaymentDetails = async (pharmacyId) => {
    if (!pharmacyId) return

    setPaymentLoading(true)
    try {
      const response = await server.get(
        `/payments/pharmacy-payment-profile/${pharmacyId}`
      )
      setPaymentDetails(response.data)
      setPaymentLoading(false)
    } catch (error) {
      console.error('Error fetching payment details:', error)
      setPaymentLoading(false)
    }
  }

  // BODY TEMPLATES
  const statusBodyTemplate = (order) => {
    let statusColor
    let textColor
    switch (order.status) {
      case 'Cancelled':
        statusColor = '#EF4444'
        textColor = '#EF4444'
        break
      case 'Pending':
        statusColor = '#86888b'
        textColor = '#86888b'

        break
      case 'Received':
        statusColor = '#FFA500'
        textColor = '#FFA500'
        break
      case 'Confirmed':
        statusColor = '#22c55e'
        textColor = '#22c55e'
        break
      default:
        statusColor = '#86888b'
        textColor = '#86888b'
        break
    }

    const showCancelHistory =
      order.status === 'Cancelled' &&
      order.cancelledFromStatus &&
      order.cancelledFromStatus !== 'Pending' &&
      order.cancelledFromStatus !== 'Cancelled'

    return (
      <div className='d-flex flex-column align-items-center'>
        <span
          className={`d-flex justify-content-center py-1 px-3 rounded-pill`}
          style={{ border: `1px solid ${statusColor}`, color: `${textColor}` }}
        >
          {order.status}
        </span>
        {showCancelHistory && (
          <small
            className='text-muted mt-1 text-center'
            style={{ fontSize: '0.72rem', lineHeight: 1.2 }}
            title={
              order.cancellationReason
                ? `Reason: ${order.cancellationReason}`
                : undefined
            }
          >
            was {order.cancelledFromStatus}
            {order.cancelledAt ? ` · ${formatDate(order.cancelledAt)}` : ''}
          </small>
        )}
      </div>
    )
  }

  // System body template for displaying system logos
  const systemBodyTemplate = (order) => {
    const handlePaymentClick = (e) => {
      e.stopPropagation()
      // Fetch payment details for the pharmacy associated with this order
      fetchPaymentDetails(order.pharmacy?.id)
      setPaymentModalVisible(true)
    }

    let systemLogo
    let systemName

    if (order.system === 'Origins Direct Instant') {
      systemLogo = originsDirectInstant
      systemName = 'Origins Direct Instant'
    } else {
      systemLogo = originsDirect
      systemName = 'Origins Direct'
    }

    return (
      <div className='d-flex justify-content-center align-items-center'>
        <img
          src={systemLogo}
          alt={systemName}
          style={{ height: '20px' }}
          className='me-2'
        />
        {order.system !== 'Origins Direct' && (
          <i
            className='mdi mdi-credit-card-outline ml-2'
            style={{
              marginLeft: '10px',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#626ED4',
            }}
            onClick={handlePaymentClick}
          ></i>
        )}
      </div>
    )
  }

  const totalInvoiceBodyTemplate = (order) =>
    formatCurrency(order.totalInvoiceAmount)
  const orderDateBodyTemplate = (order) => formatDate(order.orderDate)

  const paPercentageBodyTemplate = (order) =>
    formatCurrency(order.totalInvoicePa)

  const totalPaPercentageBodyTemplate = (order) =>
    formatPercentage(order.totalInvoicePaPercentage)

  return (
    <>
      <div className='card mb-5'>
        <DataTable
          showGridlines
          value={order}
          tableStyle={{ minWidth: '50rem' }}
        >
          <Column
            rowSpan={2}
            header='Account Number'
            field='pharmacy.user.accountNo'
            hidden={
              props.order?.supplier?.name === 'Origins Direct' ? true : false
            }
          />
          {isAdmin && (
            <Column header='Account Name' rowSpan={2} field='pharmacy.name' />
          )}
          <Column header='Supplier' field='supplier.name' rowSpan={2} />
          <Column header='Order Number' rowSpan={2} field='supplierOrderNo' />
          <Column
            header='Order Date'
            rowSpan={2}
            field='orderDate'
            body={orderDateBodyTemplate}
          />

          <Column
            header='System'
            rowSpan={2}
            field='system'
            body={systemBodyTemplate}
          />
          <Column
            header='Status'
            rowSpan={2}
            field='status'
            body={statusBodyTemplate}
          />
          <Column
            header='Order Total'
            rowSpan={2}
            field='totalInvoiceAmount'
            body={totalInvoiceBodyTemplate}
          />
          <Column
            header='PA Amount'
            rowSpan={2}
            field='totalInvoicePa'
            hidden={!canSeePa}
            body={paPercentageBodyTemplate}
          />
          <Column
            header='PA Percentage'
            rowSpan={2}
            hidden={!canSeePa}
            field='totalPaPercentage'
            body={totalPaPercentageBodyTemplate}
          />
        </DataTable>
      </div>

      {/* Payment Modal */}
      <Modals
        ModalHeader='Payment Information'
        btnName='Close'
        visible={paymentModalVisible}
        setVisible={setPaymentModalVisible}
        onButtonClick={() => setPaymentModalVisible(false)}
        width='40rem'
      >
        {paymentLoading ? (
          <div className='text-center my-3'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
            <p className='mt-2'>Loading payment details...</p>
          </div>
        ) : (
          <div>
            <div className='mb-3'>
              <p>
                {order[0]?.pyNumber
                  ? `Card ending in  ${order[0]?.pyNumber}`
                  : 'No payment information available'}
              </p>
            </div>

            <div className='small text-muted mt-3'>
              <Message
                severity='info'
                content='For security reasons, complete payment details are not displayed.'
              />
            </div>
          </div>
        )}
      </Modals>
    </>
  )
}
